import React from 'react';
import { QrCode, Zap, Clock, CreditCard, FileText, Clock3, ShieldCheck } from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: QrCode,
      title: 'QR-Based Access',
      description: 'Simply scan the QR code with your phone to start. No apps, no accounts needed.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: Zap,
      title: 'Instant Printing',
      description: 'Upload and print in seconds. Perfect for urgent documents and last-minute assignments.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: Clock,
      title: 'Scheduled Printing',
      description: 'Schedule your prints in advance and pick them up whenever convenient.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: ShieldCheck,
      title: 'Secure Document Handling',
      description: 'End-to-end encryption for your documents. Private prints with PIN protection and auto-delete after collection.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: CreditCard,
      title: 'Easy Online Payment',
      description: 'Secure digital payments with UPI, cards, and wallets. No cash hassles.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: FileText,
      title: 'Multiple Format Support',
      description: 'Supports A4 & A5 sizes. Print PDFs, documents, and images effortlessly.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: Clock3,
      title: '24/7 Availability',
      description: 'Access PRINTLEY anytime, anywhere it\'s installed. Round-the-clock service.',
      gradient: 'from-yellow-500 to-yellow-600'
    }
  ];

  return (
    <section id="features" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-yellow-500 font-semibold text-sm uppercase tracking-wide">Features</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Everything You Need, Nothing You Don't
          </h2>
          <p className="text-lg text-gray-600">
            PRINTLEY is packed with smart features designed to make printing fast, easy, and accessible for everyone.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group relative bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-yellow-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
            >
              {/* Icon Container */}
              <div className="mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-xl flex items-center justify-center group-hover:bg-yellow-500 transition-colors duration-300">
                  <feature.icon className="w-8 h-8 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>

              {/* Hover Effect Background */}
              <div className="absolute inset-0 bg-yellow-50/0 group-hover:bg-yellow-50/50 rounded-2xl transition-all duration-300 -z-10"></div>
            </div>
          ))}
        </div>

        {/* Bottom Highlight Section */}
        <div className="mt-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold mb-4">
              Why Choose PRINTLEY?
            </h3>
            <p className="text-lg text-yellow-50 mb-8">
              We're not just another printing solution. We're reimagining how people access printing services 
              in the digital age – making it faster, smarter, and more convenient than ever before.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <p className="text-4xl font-bold mb-2">3 sec</p>
                <p className="text-yellow-50">Average Print Time</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <p className="text-4xl font-bold mb-2">100%</p>
                <p className="text-yellow-50">Contactless</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <p className="text-4xl font-bold mb-2">24/7</p>
                <p className="text-yellow-50">Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
